import { Box, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { AppForm, AppFormProps, FormFileCoreUpload, FormSelectField, FormTextField } from '../../../components/FormCore'
import { FileType } from '../../../enums'
import { IPositionType } from '../../../generated-types'
import { IPositionDrawing } from '../../../generated-types/position-drawing'
import { IPositionDrawingProduct } from '../../../generated-types/position-drawing-product'
import { useZodForm } from '../../../hooks/zod-form'
import { ProductGrid } from './components/productGridComponent'
import { GenericDialogLayout } from '../../../components/GenericDialogLayout'
import { useEffect } from 'react'
import { useGetFile } from '../../../api/file'
import { MainButton } from 'src/components/MainButton'
import { useDialogCtx } from 'src/hooks/context-hooks'

export interface PositionDrawingForm extends AppFormProps {
  id?: EntityId
  title: string
}

const positionTypes = Object.keys(IPositionType)
  .filter(v => isNaN(Number(v)))
  .map(t => IPositionType[t])

export const PositionDrawingForm: React.FC<PositionDrawingForm> = ({ onSubmit, onCancel, initialValues, title }) => {
  const { t: translate } = useTranslation(['admin-position-drawing', 'common'])
  const { showConfirmDialog } = useDialogCtx()
  const positionDrawing = initialValues as IPositionDrawing
  const isUpdateForm = positionDrawing?.id
  const { data: file } = useGetFile(initialValues?.fileId)

  const schema = z
    .object({
      id: z.number().default(0),
      drawingNumber: z.string().nonempty(translate('enterValidDrawingNumber')).default(''),
      fileId: z.union([z.string(), z.number()]),
      mooringId: z.number().or(z.null()).optional(),
      category: z.number({
        required_error: translate('selectCategory'),
      }),
      bridleLegCount: z
        .union([z.string(), z.number(), z.null()])
        .optional()
        .transform(value => (typeof value === 'string' ? parseInt(value) : value)),
      positionDrawingProducts: z
        .array(z.any())
        .default([] as IPositionDrawingProduct[])
        .nullable()
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (data.category === IPositionType.Bridle) {
        if (data.bridleLegCount == null || isNaN(data.bridleLegCount)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: translate('enterValidBridleLegCount', { ns: 'position' }),
            path: ['bridleLegCount'],
          })
        } else if (data.bridleLegCount < 1 || data.bridleLegCount > 3) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: translate('enterValidBridleLegCount', { ns: 'position' }),
            path: ['bridleLegCount'],
          })
        }
      }
    })

  const form = useZodForm(schema, { defaultValues: { ...positionDrawing, mooringId: initialValues?.mooringId } })
  const { isDirty } = form.formState

  useEffect(() => {
    const currentFormValues = form.getValues()

    if (initialValues != undefined && initialValues != null) {
      currentFormValues.positionDrawingProducts = initialValues.positionDrawingProducts
    }

    form.reset(currentFormValues)
  }, [form, initialValues])

  const handleCancel = () => {
    if (!isDirty) {
      onCancel?.()
      return
    }
    showConfirmDialog(
      translate('discardPositionDrawingChanges'),
      translate('areYouSureDiscardPositionDrawingChanges'),
      { acceptText: translate('yes', { ns: 'common' }), cancelText: translate('no', { ns: 'common' }) },
      pop => {
        pop()
        onCancel?.()
      },
    )
  }

  const category = form.watch('category') as IPositionType

  return (
    <GenericDialogLayout
      title={title}
      pop={handleCancel}
      actions={[
        <MainButton
          key={'action-btn-position-drawing'}
          variant="contained"
          onClick={() => {
            const productValues = form
              .getValues()
              .positionDrawingProducts?.filter(
                x => x.quantity != undefined && x.positionDrawingNumber != undefined && x.productId != undefined,
              )
            form.setValue('positionDrawingProducts', productValues)
            form.handleSubmit(onSubmit)()
          }}
        >
          {isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        </MainButton>,
      ]}
      removedCloseButton={false}
    >
      <AppForm form={form} onSubmit={onSubmit} hasInitialValues={initialValues}>
        {isUpdateForm && <FormTextField name="id" sx={{ display: 'none' }} />}
        <Grid container spacing={1}>
          <Grid item xs={category === IPositionType.Bridle ? 4 : 6}>
            <FormTextField name="drawingNumber" label={translate('drawingNumber')} />
          </Grid>
          <Grid item xs={category === IPositionType.Bridle ? 4 : 6}>
            <FormSelectField
              name="category"
              data={positionTypes.filter(item => item !== IPositionType.Cage)}
              label={translate('category', { ns: 'common' })}
              getOptionLabel={option => translate(IPositionType[option], { ns: 'position' })}
              renderOption={(props: any, option) => (
                <Box {...props} key={option}>
                  {translate(IPositionType[option], { ns: 'position' })}
                </Box>
              )}
              defaultValue={positionTypes[initialValues?.category ?? IPositionType.FrameLines]}
            />
          </Grid>
          {category === IPositionType.Bridle && (
            <Grid item xs={4}>
              <FormTextField
                name="bridleLegCount"
                type="number"
                label={translate('bridleLegCount', { ns: 'position' })}
                sx={{ width: '100%' }}
                InputProps={{ inputProps: { min: 1, max: 3 } }}
              />
            </Grid>
          )}
        </Grid>
        <FormFileCoreUpload
          label={translate('fileName', { ns: 'common' })}
          name="fileId"
          mapFileKey="id"
          type={FileType.Image}
          initialFiles={file}
          height="95px"
          iconHeight={'35px'}
          titleFontSize="14px"
          subtitleFontSize={'12px'}
        />
        <Box py={1}>
          <ProductGrid />
        </Box>
      </AppForm>
    </GenericDialogLayout>
  )
}
